import { buildBillingInformationInput } from "../../components/payments/util";
import { billingInfoFieldSchema } from "../../schemas/billingInfoSchema";
import { buildValidationObject, ERROR_CODES, ERROR_TYPES } from "../../util";

/**
 * Class representing the base payment method, with billing information functionality.
 */
export class PaymentMethod {
	/**
	 * @param {object} billingAddressConfigs - User supplied configurations.
	 * @param {theme} customTheme - OdinPay theme object.
	 * @param {Function} onChangeValidation - custom error handler for onchange field validation
	 */
	constructor(billingAddressConfigs, customTheme, onChangeValidation) {
		this.billingInformation = {};
		this.theme = customTheme;
		this.billingAddressConfigs = billingAddressConfigs;
		this.onChangeValidation = onChangeValidation;
	}

	/**
	 * @typedef {Object} BillingInformationObject
	 * @property {HTMLElement} inputElement
	 * @property {Boolean} touched
	 * @property {Boolean} error
	 */

	initializeBillingInformation = (billingAddressConfigs, customTheme) => {
		let newInputs = {};
		Object.entries(billingAddressConfigs).forEach(([fieldKey, value]) => {
			/**
			 * @type {Object.<String, BillingInformationObject>}
			 */
			newInputs = {
				...newInputs,
				[fieldKey]: buildBillingInformationInput({
					name: fieldKey,
					config: value,
					onChange: this.onChange,
					theme: customTheme,
					onBlur: this.onBlur
				})
			};
			if (value?.initialValue) {
				this.billingInformation = {
					...this.billingInformation,
					[fieldKey]: value?.initialValue
				};
			}
		});
	};

	/**
	 * @type {function} onChange - for use on Billing Information fields
	 * @param {string} billingInformationKey - the billing information property to change
	 * @param {string} billingInformationValue - the billing information property's value
	 */
	onChange = async (billingInformationKey, billingInformationValue) => {
		this.billingInformation = {
			...this.billingInformation,
			[billingInformationKey]: billingInformationValue
		};

		let isComplete = true;
		try {
			await billingInfoFieldSchema[billingInformationKey]?.(true).validate(billingInformationValue, {
				abortEarly: false
			});
		} catch {
			isComplete = false;
		}

		this.onChangeValidation(
			buildValidationObject({
				type: ERROR_TYPES.FIELD_VALIDATION,
				isValid: true,
				fieldName: billingInformationKey,
				selector: this.billingAddressConfigs[billingInformationKey]?.selector,
				isComplete
			})
		);
	};

	onBlur = async (billingInformationKey, billingInformationValue) => {
		let error = {};
		try {
			await billingInfoFieldSchema[billingInformationKey]?.(true).validate(billingInformationValue, {
				abortEarly: false
			});
			error = buildValidationObject({
				type: ERROR_TYPES.FIELD_VALIDATION,
				isValid: true,
				fieldName: billingInformationKey,
				selector: this.billingAddressConfigs[billingInformationKey]?.selector,
				isComplete: true
			});
		} catch (err) {
			// Handle validation errors
			error = buildValidationObject({
				type: ERROR_TYPES.FIELD_VALIDATION,
				isValid: false,
				fieldName: billingInformationKey,
				selector: this.billingAddressConfigs[billingInformationKey]?.selector,
				errorCode: err.inner[0]?.errors?.[0],
				isComplete: false
			});
		}
		this.onChangeValidation(error);
	};

	handleOdinResponse = (odinResponse, odinError) => {
		let updatedApiResponse;
		if (odinResponse) {
			updatedApiResponse = {
				success: true,
				paymentMethod: odinResponse
			};
		} else {
			updatedApiResponse = {
				success: false,
				errors: [
					{
						type: ERROR_TYPES.BACKEND,
						errorCode: odinError?.statusCode === 401 ? ERROR_CODES.UNAUTHORIZED : ERROR_CODES.VALIDATION_ERROR,
						message: odinError.message || "",
						errors: odinError.data?.errors || []
					}
				]
			};
		}
		return updatedApiResponse;
	};

	handleBtError = (error, type) => {
		switch (error.status) {
			case 401:
				return {
					success: false,
					errors: [{ type: ERROR_TYPES.BACKEND, errorCode: ERROR_CODES.UNAUTHORIZED, message: "", errors: [] }]
				};
			case 400:
				return {
					success: false,
					errors: [
						{
							type: ERROR_TYPES.BACKEND,
							errorCode: ERROR_CODES.TOKENIZATION_FAILED,
							message: "",
							errors: [Object.values(error?.data?.errors || {})?.[0]] || []
						}
					]
				};
			default:
				return {
					success: false,
					errors: [
						{ type: ERROR_TYPES.BACKEND, errorCode: ERROR_CODES.TOKENIZATION_FAILED, message: error.name, errors: [] }
					]
				};
		}
	};
}
